/**
 * Shared Swiper-carousel construction logic — extracted from
 * elementor-slider.js so the Products widget's own frontend script
 * (elementor-products.js) can reuse the exact same options-building/
 * construction code instead of duplicating it, per explicit user request
 * (see trait-gws-elementor-swiper-carousel.php for the PHP-side half of
 * this same extraction).
 *
 * Reads the `data-gws-carousel-*` attributes any widget using the shared
 * PHP trait's render_carousel_data_attributes() prints on its `.swiper`
 * element, and targets the shared `.gws-carousel-arrow-prev`/`-next`/
 * `.gws-carousel-pagination` classes render_carousel_nav_markup() prints.
 *
 * Deliberately NOT editor-aware — no isEditMode() branching here at all.
 * Consumers decide for themselves whether/how to gate editor behavior
 * (elementor-slider.js keeps its own live-only guard and delegates into
 * this file only on the live frontend; elementor-products.js has no such
 * split at all, since Products has no Nested-Elements/Editing-Mode
 * concerns — see the Elementor migration plan for the full rationale).
 *
 * Also resolves the optional "drive this slider from a different widget"
 * override (nav_prev_selector/nav_next_selector/nav_pagination_selector
 * controls — see register_carousel_nav_controls() in
 * trait-gws-elementor-swiper-carousel.php for the user-facing how-to).
 * Swiper doesn't care what element it controls: `navigation.nextEl`/
 * `prevEl` just need a click target, no markup requirements at all;
 * `pagination.el` just needs an element it's allowed to write dot markup
 * into. A custom target can be ANYWHERE on the page — not necessarily
 * inside this widget's own DOM — so those three are looked up via
 * `document.querySelector`, unlike the built-in elements (`el.querySelector`,
 * scoped to this widget instance so multiple carousels on one page don't
 * collide on the shared default class names).
 */
( function () {
	'use strict';

	window.GWSBuildSwiperCarouselOptions = function ( el ) {
		var attr = function ( name ) {
			return el.getAttribute( 'data-gws-carousel-' + name );
		};

		var autoplayDelay = parseInt( attr( 'autoplay' ), 10 ) || 0;
		var pauseOnHover = attr( 'pause-on-hover' ) === '1';
		var hasArrows = attr( 'arrows' ) === '1';
		var hasPagination = attr( 'pagination' ) === '1';
		var effect = attr( 'effect' ) || 'slide';

		var breakpoints = {};
		try {
			breakpoints = JSON.parse( attr( 'breakpoints' ) || '{}' );
		} catch ( e ) {
			breakpoints = {};
		}

		var options = {
			loop: attr( 'loop' ) === '1',
			rewind: attr( 'rewind' ) === '1',
			speed: parseInt( attr( 'speed' ), 10 ) || 0,
			effect: effect,
			slidesPerView: parseFloat( attr( 'slides-per-view' ) ) || 1,
			slidesPerGroup: parseInt( attr( 'slides-per-group' ), 10 ) || 1,
			initialSlide: parseInt( attr( 'initial-slide' ), 10 ) || 0,
			spaceBetween: parseInt( attr( 'space-between' ), 10 ) || 0,
			centeredSlides: attr( 'centered-slides' ) === '1',
			direction: attr( 'direction' ) || 'horizontal',
			grabCursor: attr( 'grab-cursor' ) === '1',
			// Swiper's own default (true) calls event.stopPropagation() -
			// in the CAPTURE phase, on the .swiper element itself - for any
			// click that lands while swiper.animating is true, specifically
			// to stop a drag-to-swipe gesture from accidentally triggering
			// a link/button inside a slide. Since capture-phase
			// stopPropagation() halts the event before it ever reaches the
			// bubble phase, this silently breaks event DELEGATION on any
			// ancestor above `.swiper` (no error, the event just never
			// arrives) - confirmed as the real cause of the Testimonials
			// widget's "Read More" button doing nothing whenever a click
			// happened to land during a transition. Disabled for every
			// carousel using this shared builder (Slider, Products,
			// Testimonials, and any future one) - genuinely interactive
			// content inside a slide should always be clickable, not just
			// when the carousel happens to be at rest. `preventClicks`
			// (Swiper's OTHER, separate option - calls preventDefault()
			// only, doesn't stop propagation) is left at its own default,
			// still a reasonable guard against an accidental native link
			// navigation mid-drag.
			preventClicksPropagation: false,
		};

		if ( Object.keys( breakpoints ).length > 0 ) {
			options.breakpoints = breakpoints;
		}

		if ( 'fade' === effect ) {
			options.fadeEffect = { crossFade: true };
		}

		if ( autoplayDelay > 0 ) {
			options.autoplay = {
				delay: autoplayDelay,
				disableOnInteraction: false,
				pauseOnMouseEnter: pauseOnHover,
				// Swiper's OWN default here is false - meaning autoplay
				// restarts from the first slide once it reaches the last
				// one, EVEN WITH loop and rewind both off. Confirmed as
				// the actual cause of a real bug report: "rewinds back
				// once it reaches the end" with loop/rewind both
				// correctly false in swiper.params. Only skip stopping
				// (i.e. let it wrap) when loop or rewind is actually
				// handling that transition properly - with both off, it
				// should just stop dead at the last slide, per explicit
				// user direction.
				stopOnLastSlide: ! options.loop && ! options.rewind,
			};
		}

		if ( hasArrows ) {
			var prevSelector = attr( 'nav-prev-selector' );
			var nextSelector = attr( 'nav-next-selector' );

			options.navigation = {
				nextEl: nextSelector ? document.querySelector( nextSelector ) : el.querySelector( '.gws-carousel-arrow-next' ),
				prevEl: prevSelector ? document.querySelector( prevSelector ) : el.querySelector( '.gws-carousel-arrow-prev' ),
			};
		}

		if ( hasPagination ) {
			var paginationSelector = attr( 'pagination-selector' );
			var paginationEl = paginationSelector ? document.querySelector( paginationSelector ) : el.querySelector( '.gws-carousel-pagination' );

			// A custom target (e.g. an empty Elementor Container the user
			// positioned themselves) won't already carry this class — add
			// it so the widget's own Dot Size/Spacing/Color style controls
			// (which target `.gws-carousel-pagination .swiper-pagination-bullet`
			// in dist/css/elementor-swiper-carousel.css) still apply. A
			// no-op on the built-in element, which already has the class
			// from render_carousel_nav_markup().
			if ( paginationEl ) {
				paginationEl.classList.add( 'gws-carousel-pagination' );
			}

			options.pagination = {
				el: paginationEl,
				clickable: true,
			};
		}

		if ( attr( 'keyboard' ) === '1' ) {
			options.keyboard = { enabled: true };
		}

		if ( attr( 'mousewheel' ) === '1' ) {
			options.mousewheel = { enabled: true };
		}

		return options;
	};

	window.GWSInitSwiperCarousel = function ( el ) {
		if ( el.swiper ) {
			el.swiper.destroy( true, true );
		}

		var options = window.GWSBuildSwiperCarouselOptions( el );
		var swiperInstance = new window.Swiper( el, options );

		// Swiper computes how many DUPLICATE slides loop mode needs from
		// slidesPerView at construction time - when a responsive breakpoint
		// later changes slidesPerView (mobile's smaller value -> tablet's/
		// desktop's larger one), Swiper's own breakpoint-switching does NOT
		// reliably recompute that duplicate count. This is a known Swiper
		// limitation, not something wrong in our own settings/markup -
		// confirmed live: loop mode intermittently breaking specifically
		// around breakpoint transitions ("flaky, sometimes works sometimes
		// not"), worse than a simple wrong-value bug would produce.
		// loopDestroy()+loopCreate() on Swiper's own 'breakpoint' event
		// (fired whenever the active breakpoint tier changes) is the
		// standard community workaround - forces loop's slide duplication
		// to rebuild against whatever slidesPerView just became active.
		if ( options.loop ) {
			swiperInstance.on( 'breakpoint', function () {
				swiperInstance.loopDestroy();
				swiperInstance.loopCreate();
			} );
		}

		return swiperInstance;
	};
} )();
